/**
 * Thin fetch wrapper for the Battle API.
 * All requests include credentials so the HttpOnly session cookie is sent.
 * All paths are relative (/api/...) — works in dev (proxied by Vite) and production.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as Record<string, unknown>).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, detail);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ---- shared types (mirror server JSON shapes) ----------------------------

export type UserRole = 'student' | 'sme' | 'admin';

export interface SessionResp {
  authenticated: boolean;
  student?: { id: string; email: string; name: string; role: UserRole };
}

export interface LoginResp {
  authenticated: true;
  student: { id: string; email: string; name: string; role: UserRole };
}

export interface ClusterState {
  clusterId: string;
  fullName: string;
  paper: number;
  world: number;
  displayOrder: number;
  prerequisites: string[];
  portalTargets: string[];
  difficultyBoundaryTier: string;
  active: boolean;
  locked: boolean;
  // M13 — true when the student's effective tier may not consume this cluster.
  tierLocked: boolean;
  touched: boolean;
  stageCleared: boolean;
  clearedLatched: boolean;
  completionPct: number;
  minibossAvailable: boolean;
  minibossCleared: boolean;
  stagebossCleared: boolean;
  finalbossAvailable: boolean;
  finalbossCleared: boolean;
  regularBattlesCleared: number;
  regularBattlesTotal: number;
  bestScores: { miniboss: number | null; stageboss: number | null; finalboss: number | null };
  // FIX 3: distinct concept question counts (matches battle screen clusterProgress)
  conceptCleared: number;
  conceptTotal: number;
  conceptPct: number;
}

export interface PortalState {
  portalKey: string;
  isPermanent: boolean;
  tier: 6 | 9 | 12;
  passBar: number;
  available: boolean;
  // M13 — true when the student's effective tier is not entitled to both endpoints.
  tierLocked: boolean;
  // M9-patch3: cluster-style N/M progress.
  solved: number;
  total: number;
  // Kept for the Final-Boss gate (server-side); not surfaced in the M9-patch3 UI.
  deployCount: number;
  // FIX D: endpoint cluster identifiers and their cleared status (from map.ts)
  endpointA: string;
  endpointB: string;
  aCleared: boolean;
  bCleared: boolean;
}

export interface MapResp {
  clusters: ClusterState[];
  portals: PortalState[];
  world2Unlocked: boolean;
}

export interface Question {
  id: string;
  clusterId: string;
  battleType: string;
  markValue: number;
  commandWord: string | null;
  conceptTags: string[];
  portalClusters: string[];
  stem: string;
  options?: { option: string; text: string }[];
  // Phase 4.2 M4: server pre-computes whether the [📖] Study button has content
  // for this question's concept tags. Older builds may omit it — treat as false.
  hasStudyContent?: boolean;
}

// ---- Phase 4.2 M4 — Study panel ----------------------------------------

export interface StudyConceptBody {
  examiner_definition?: string;
  key_distinctions?: string[];
  how_tested?: string;
  diagram_notes?: string;
  worked_examples?: Array<{ prompt?: string; solution?: string } | string>;
  applications?: string[] | string;
  exam_link?: string;
  // Defensive: accept any extra keys without breaking the renderer.
  [k: string]: unknown;
}

export interface StudyConcept {
  clusterId: string;
  conceptCode: string;
  conceptTitle: string;
  specRequirement: string | null;
  coreDefinition: string | null;
  body: StudyConceptBody | null;
  registryTerm: string | null;
  portalLinks: string[] | null;
  sourceQuality: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'RETIRED';
  // Phase 4.2 M4: SME notes is a row-level column on study_content.
  smeNotes: string | null;
}

export interface StudyConceptsResp {
  ok: true;
  concepts: StudyConcept[];
  missing: string[];
}

export interface NextResp {
  question: Question;
  attemptNumber: number;
  maxAttempts: number;
  clusterProgress?: { total: number; cleared: number; remaining: number };
  // M10 — when false, the client blocks paste into answer inputs. Older builds
  // may omit it — treat as true (paste allowed).
  pasteEnabled?: boolean;
}

export interface PortalNextResp {
  question: Question;
  attemptNumber: number;
  maxAttempts: number;
  // M9-patch3: cluster-style N/M progress replaces clearedOnce + deployCount.
  portalProgress: { tier: number; passBar: number; solved: number; total: number };
  // M10 — paste gate (see NextResp.pasteEnabled).
  pasteEnabled?: boolean;
}

export interface AoBreakdown {
  ao1?: number;
  ao2?: number;
  ao3?: number;
  ao4?: number;
  ao2Level?: number;
  ao3Level?: number;
  ao4Level?: number;
}

export interface GradeResult extends AoBreakdown {
  score: number;
  marks: number;
  cleared: boolean;
  chainStepsDetected?: number;
  conditionStmtPresent?: boolean;
  perfectStrike: boolean;
  feedback: string;
  modelAnswer?: string;
  providerUsed: string;
  markingLatencyMs: number;
}

export interface SubmitResp {
  gradeResult: GradeResult;
  xpAwarded: number;
  newLevel: number;
  previousLevel: number;
  newBadges: string[];
  attemptNumber: number;
  maxAttempts: number;
  isFinalAttempt: boolean;
  conceptTags: string[];
  portalClusters: string[];
  portalDeployed: boolean;
}

// FIX 2c: Review endpoint types
export interface ReviewAttempt {
  templateId: string;
  attemptNumber: number;
  battleType: string;
  markValue: number;
  questionText: string;
  studentResponse: string;
  score: number | null;
  ao1: number | null;
  ao2: number | null;
  ao3: number | null;
  ao4: number | null;
  cleared: boolean;
  createdAt: string;
  modelAnswer?: string;
}

export interface ReviewResp {
  cluster: string;
  attempts: ReviewAttempt[];
}

export interface ProfileResp {
  student: { id: string; email: string; name: string; role?: UserRole };
  gameState: {
    totalXp: number;
    level: number;
    currentStreakDays: number;
    longestStreakDays: number;
    perfectStrikeCount: number;
    portalMasterCount: number;
    chainReactionCount: number;
    totalRetries: number;
  };
  badges: { code: string; unlockedAt: string }[];
  leaderboard: { rank: number | null; totalPlayers: number | null };
  examCountdown: { name: string; date: string } | null;
}

// ---- Phase 4.2 M10 — Leaderboard ---------------------------------------

export interface LeaderboardEntry {
  rank: number;
  name: string;
  level: number;
  totalXp: number;
  badges: string[];
}

export interface LeaderboardSelf {
  rank: number;
  name: string;
  level: number;
  totalXp: number;
  badges: string[];
  totalPlayers: number;
}

export interface LeaderboardResp {
  enabled: boolean;
  entries?: LeaderboardEntry[];
  self?: LeaderboardSelf | null;
}

/** GET /api/leaderboard — global leaderboard + self row (see server route). */
export function fetchLeaderboard(): Promise<LeaderboardResp> {
  return api.get<LeaderboardResp>('/api/leaderboard');
}
